import * as Sentry from '@sentry/browser'

export default defineNuxtPlugin((nuxtApp) => {
  const { getEffectiveAuthToken } = useEffectiveAuthToken()
  // @ts-expect-error apollo hook typing missing in module definitions
  nuxtApp.hook('apollo:auth', async ({ token }) => {
    token.value = await getEffectiveAuthToken()
  })
  // @ts-expect-error apollo hook typing missing in module definitions
  nuxtApp.hook('apollo:error', (error) => {
    const { operation, networkError, graphQLErrors } = error

    // '''networkError.result.errors''' indicates that the server returned GraphQL errors, this is a temporal fix to avoid spamming auth errors when we receive GraphQL errors.
    if (networkError && !networkError.result.errors) {
      manageNetworkError(networkError, operation)
    }

    if (graphQLErrors && graphQLErrors.length > 0) {
      graphQLErrors.forEach((gqlError: any) => {
        manageGraphQLError(gqlError, operation)
      })
    }
  })
})

const manageNetworkError = (networkError: any, operation: any) => {
  const snackbar = useSnackbar()
  const statusCode = networkError.statusCode

  switch (statusCode) {
    case 401:
      manageAuthError(networkError, operation)
      break
    case 403:
      snackbar.error('You do not have permission to perform this action.')
      break
    case 404:
      snackbar.error('The requested resource was not found.')
      break
    case 500:
    case 502:
    case 503:
    case 504:
      snackbar.error('A server error occurred. Please try again later.')
      reportToSentry(networkError, operation, 'network_error')
      break
    default:
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        snackbar.warning('No internet connection. Please check your network.')
      } else {
        snackbar.error('Unable to connect to the server. Please try again later.')
      }
      reportToSentry(networkError, operation, 'network_error')
  }
}

const manageGraphQLError = (error: any, operation: any) => {
  const snackbar = useSnackbar()
  const errorCode = error.extensions?.code || ''

  switch (errorCode) {
    case 'UNAUTHENTICATED':
    case 'FORBIDDEN':
      manageAuthError(error, operation)
      break
    case 'BAD_USER_INPUT':
    case 'VALIDATION_ERROR':
      snackbar.warning(error.message || 'Please check your input')
      break
    case 'INTERNAL_SERVER_ERROR':
      snackbar.error('A server error occurred. Our team has been notified.')
      reportToSentry(error, operation, 'graphql_error')
      break
  }
}

const manageAuthError = (error: any, operation: any) => {
  const snackbar = useSnackbar()
  snackbar.error('Your session has expired. Please log in again.')
  reportToSentry(error, operation, 'auth_error', 'info')
}

const reportToSentry = (error: any, operation: any, errorType: string, level: 'error' | 'warning' | 'info' = 'error') => {
  Sentry.captureException(error, {
    level,
    contexts: {
      graphql: {
        operationName: operation.operationName,
        variables: operation.variables,
        errorType,
        statusCode: error.statusCode || error.extensions?.statusCode
      }
    },
    tags: {
      graphql_operation: operation.operationName || 'unknown',
      error_type: errorType
    }
  })
}
